/**
 * USDA Hardiness Zone Detector
 * Detects visitor location and displays their USDA hardiness zone
 *
 * PERFORMANCE OPTIMIZATIONS:
 * - Skips all API calls for returning visitors using localStorage cache (30-day TTL)
 * - Caches individual ZIP lookups to avoid repeat network requests
 * - Widget only initializes on pages that actually contain [data-hardiness-zone]
 * - container reference stored once at init — no repeated querySelector calls
 * - Load this script with `defer` in your HTML: <script src="..." defer></script>
 */

class HardinessZoneDetector {
  constructor() {
    this.container = null;
    this.CACHE_TTL = 30 * 24 * 60 * 60 * 1000;
  }

  _cacheGet(key) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const { value, expiry } = JSON.parse(raw);
      if (expiry && Date.now() > expiry) {
        localStorage.removeItem(key);
        return null;
      }
      return value;
    } catch {
      return null;
    }
  }

  _cacheSet(key, value, ttl = this.CACHE_TTL) {
    try {
      localStorage.setItem(key, JSON.stringify({ value, expiry: Date.now() + ttl }));
    } catch {}
  }

  _cacheClear(key) {
    try { localStorage.removeItem(key); } catch {}
  }

  async init() {
    this.container = document.querySelector('[data-hardiness-zone]');
    if (!this.container) return;

    const cached = this._cacheGet('hz_result');
    if (cached) {
      this.displayZone(this.container, cached.zone, cached.location);
      return;
    }

    this.showLoading(this.container);

    try {
      const location = await this.getLocationByIP();
      const zone = await this.getHardinessZone(location);
      this._cacheSet('hz_result', { zone, location });
      this.displayZone(this.container, zone, location);
    } catch (error) {
      console.error('Error detecting hardiness zone:', error);
      this.showError(this.container);
    }
  }

  async getLocationByIP() {
    const response = await fetch('https://ipapi.co/json/');
    if (!response.ok) throw new Error('IP geolocation request failed');
    const data = await response.json();
    if (!data.latitude || !data.longitude) throw new Error('Invalid location data');
    return {
      lat: data.latitude,
      lon: data.longitude,
      city: data.city,
      region: data.region,
      country: data.country_code,
      method: 'ip'
    };
  }

  async getHardinessZone(location) {
    if (location.country && location.country !== 'US') {
      return {
        zone: 'N/A',
        description: 'USDA zones are for United States only',
        tempRange: '',
        location
      };
    }
    const zone = this.calculateZoneFromCoordinates(location.lat, location.lon);
    return {
      zone: zone.code,
      description: zone.description,
      tempRange: zone.tempRange,
      location
    };
  }

  calculateZoneFromCoordinates(lat, lon) {
    // West Coast (coastal influence makes zones warmer than latitude alone)
    if (lon < -120) {
      if (lat >= 48.5) return { code: '8b', tempRange: '15°F to 20°F', description: 'Pacific Northwest Coast' };
      if (lat >= 47) return { code: '9a', tempRange: '20°F to 25°F', description: 'Puget Sound/Portland' };
      if (lat >= 45) return { code: '9a', tempRange: '20°F to 25°F', description: 'Western Oregon' };
      if (lat >= 42) return { code: '9b', tempRange: '25°F to 30°F', description: 'Northern California Coast' };
      if (lat >= 38) return { code: '10a', tempRange: '30°F to 35°F', description: 'San Francisco Bay Area' };
      if (lat >= 34) return { code: '10b', tempRange: '35°F to 40°F', description: 'Southern California Coast' };
      if (lat >= 32) return { code: '11a', tempRange: '40°F to 45°F', description: 'San Diego/Southern CA' };
    }
    
    // Mountain West (colder due to elevation)
    if (lon >= -120 && lon < -104 && lat >= 40) {
      if (lat >= 48) return { code: '4a', tempRange: '-30°F to -25°F', description: 'Northern Rockies' };
      if (lat >= 45) return { code: '5a', tempRange: '-20°F to -15°F', description: 'Montana/Idaho' };
      if (lat >= 42) return { code: '5b', tempRange: '-15°F to -10°F', description: 'Wyoming/Utah' };
      if (lat >= 40) return { code: '6a', tempRange: '-10°F to -5°F', description: 'Colorado' };
    }
    
    // Great Plains (continental climate)
    if (lon >= -104 && lon < -95) {
      if (lat >= 48) return { code: '3b', tempRange: '-35°F to -30°F', description: 'North Dakota' };
      if (lat >= 45) return { code: '4a', tempRange: '-30°F to -25°F', description: 'South Dakota' };
      if (lat >= 42) return { code: '5a', tempRange: '-20°F to -15°F', description: 'Nebraska' };
      if (lat >= 39) return { code: '5b', tempRange: '-15°F to -10°F', description: 'Kansas' };
      if (lat >= 36) return { code: '6b', tempRange: '-5°F to 0°F', description: 'Oklahoma' };
      if (lat >= 32) return { code: '7b', tempRange: '5°F to 10°F', description: 'North Texas' };
    }
    
    // Midwest
    if (lon >= -95 && lon < -80) {
      if (lat >= 47) return { code: '3b', tempRange: '-35°F to -30°F', description: 'Minnesota' };
      if (lat >= 44) return { code: '4b', tempRange: '-25°F to -20°F', description: 'Wisconsin' };
      if (lat >= 42) return { code: '5b', tempRange: '-15°F to -10°F', description: 'Iowa/Illinois' };
      if (lat >= 39) return { code: '6a', tempRange: '-10°F to -5°F', description: 'Missouri' };
      if (lat >= 36) return { code: '7a', tempRange: '0°F to 5°F', description: 'Arkansas' };
      if (lat >= 33) return { code: '8a', tempRange: '10°F to 15°F', description: 'Mississippi' };
      if (lat >= 30) return { code: '8b', tempRange: '15°F to 20°F', description: 'Louisiana' };
    }
    
    // Northeast
    if (lon >= -80 && lon < -70) {
      if (lat >= 47) return { code: '3b', tempRange: '-35°F to -30°F', description: 'Northern Maine' };
      if (lat >= 45) return { code: '4b', tempRange: '-25°F to -20°F', description: 'Maine' };
      if (lat >= 43) return { code: '5b', tempRange: '-15°F to -10°F', description: 'New Hampshire/Vermont' };
      if (lat >= 42) return { code: '6a', tempRange: '-10°F to -5°F', description: 'Massachusetts' };
      if (lat >= 41) return { code: '6b', tempRange: '-5°F to 0°F', description: 'Connecticut' };
      if (lat >= 40) return { code: '7a', tempRange: '0°F to 5°F', description: 'New York/Pennsylvania' };
      if (lat >= 39) return { code: '7b', tempRange: '5°F to 10°F', description: 'Maryland' };
    }
    
    // Mid-Atlantic
    if (lon >= -80 && lat >= 36 && lat < 40) {
      return { code: '7a', tempRange: '0°F to 5°F', description: 'Virginia/West Virginia' };
    }
    
    // Southeast
    if (lon >= -85 && lat < 36) {
      if (lat >= 35) return { code: '7a', tempRange: '0°F to 5°F', description: 'North Carolina' };
      if (lat >= 33) return { code: '8a', tempRange: '10°F to 15°F', description: 'South Carolina/Georgia' };
      if (lat >= 30) return { code: '8b', tempRange: '15°F to 20°F', description: 'Northern Florida' };
      if (lat >= 28) return { code: '9b', tempRange: '25°F to 30°F', description: 'Central Florida' };
      if (lat >= 26) return { code: '10a', tempRange: '30°F to 35°F', description: 'South Florida' };
      return { code: '10b', tempRange: '35°F to 40°F', description: 'Miami/Keys' };
    }
    
    // Gulf Coast
    if (lat >= 28 && lat < 31 && lon >= -95 && lon < -85) {
      return { code: '9a', tempRange: '20°F to 25°F', description: 'Gulf Coast' };
    }
    
    // Southwest/Desert
    if (lon >= -115 && lon < -103 && lat >= 32 && lat < 40) {
      if (lat >= 37) return { code: '6b', tempRange: '-5°F to 0°F', description: 'High Desert' };
      if (lat >= 35) return { code: '7a', tempRange: '0°F to 5°F', description: 'New Mexico' };
      return { code: '8b', tempRange: '15°F to 20°F', description: 'Arizona/Desert Southwest' };
    }
    
    // Texas
    if (lon >= -106 && lon < -93 && lat >= 26 && lat < 36) {
      if (lat >= 34) return { code: '7a', tempRange: '0°F to 5°F', description: 'Texas Panhandle' };
      if (lat >= 31) return { code: '8a', tempRange: '10°F to 15°F', description: 'North/Central Texas' };
      if (lat >= 28) return { code: '9a', tempRange: '20°F to 25°F', description: 'South Texas' };
      return { code: '9b', tempRange: '25°F to 30°F', description: 'Rio Grande Valley' };
    }
    
    // Alaska
    if (lat >= 55) {
      if (lon < -140) return { code: '7a', tempRange: '0°F to 5°F', description: 'Southeast Alaska Coast' };
      return { code: '2a', tempRange: '-50°F to -45°F', description: 'Alaska' };
    }
    
    // Hawaii
    if (lat >= 19 && lat <= 22 && lon >= -160 && lon <= -154) {
      return { code: '11b', tempRange: '45°F to 50°F', description: 'Hawaii' };
    }

    // Fallback based on latitude
    if (lat >= 48) return { code: '5a', tempRange: '-20°F to -15°F', description: 'Northern US' };
    if (lat >= 42) return { code: '6a', tempRange: '-10°F to -5°F', description: 'Mid-latitude' };
    if (lat >= 36) return { code: '7a', tempRange: '0°F to 5°F', description: 'Moderate' };
    if (lat >= 30) return { code: '8b', tempRange: '15°F to 20°F', description: 'Southern US' };
    return { code: '9a', tempRange: '20°F to 25°F', description: 'Subtropical' };
  }

  async _resolveZip(zip) {
    const cacheKey = `hz_zip_${zip}`;
    const cached = this._cacheGet(cacheKey);
    if (cached) return cached;

    const geoResponse = await fetch(`https://api.zippopotam.us/us/${zip}`);
    if (!geoResponse.ok) throw new Error('ZIP code not found');

    const geoData = await geoResponse.json();
    const place = geoData.places[0];

    const location = {
      lat: parseFloat(place.latitude),
      lon: parseFloat(place.longitude),
      city: place['place name'],
      region: place['state abbreviation'],
      zip,
      method: 'zip'
    };

    const zone = await this.getHardinessZone(location);
    const result = { zone, location };
    this._cacheSet(cacheKey, result, 365 * 24 * 60 * 60 * 1000);
    return result;
  }

  showLoading(container) {
    container.innerHTML = `
      <div class="hardiness-zone-loading">
        <span class="hardiness-zone-spinner"></span>
        <span>Detecting your zone...</span>
      </div>
    `;
  }

  displayZone(container, zoneData, location) {
    const locationText = location && location.city && location.region
      ? `${location.city}, ${location.region}`
      : 'your location';

    const compatibility = this.checkProductCompatibility(zoneData.zone);

    let statusClass = '';
    let compatibilityMessage = '';

    if (compatibility) {
      statusClass = compatibility.compatible ? 'zone-compatible' : 'zone-incompatible';
      compatibilityMessage = `
        <div class="hardiness-compatibility-message">
          <p>${compatibility.message}</p>
        </div>
      `;
    }

    container.innerHTML = `
      <div class="hardiness-zone-result ${statusClass}">
        <div class="hardiness-zone-badge">
          <span class="hardiness-zone-label">Your USDA Zone:</span>
          <span class="hardiness-zone-number">${zoneData.zone}</span>
        </div>
        <div class="hardiness-zone-details">
          <p class="hardiness-zone-temp">Coldest winter temps: ${zoneData.tempRange}</p>
          <p class="hardiness-zone-location">Based on ${locationText}</p>
        </div>
        ${compatibilityMessage}

        <div class="hardiness-zone-change-section">
          <button class="hardiness-zone-change-toggle" onclick="
            const s = document.getElementById('hz-zip-section');
            s.style.display = s.style.display === 'none' ? 'block' : 'none';
          ">Check a different location</button>
          <div id="hz-zip-section" style="display:none">
            <div class="hardiness-zone-zip-lookup">
              <input type="text"
                     id="zip-input-inline"
                     class="hardiness-zone-zip-input-inline"
                     placeholder="Enter ZIP code"
                     maxlength="5"
                     pattern="[0-9]*">
              <button class="hardiness-zone-lookup-btn" onclick="hardinessZoneDetector.lookupZipCodeInline()">
                Look up
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  async lookupZipCodeInline() {
    const zipInput = document.getElementById('zip-input-inline');
    const zip = zipInput ? zipInput.value.trim() : '';

    if (!zip || !/^\d{5}$/.test(zip)) {
      alert('Please enter a valid 5-digit ZIP code');
      return;
    }

    this.showLoading(this.container);

    try {
      const { zone, location } = await this._resolveZip(zip);
      this._cacheSet('hz_result', { zone, location });
      this.displayZone(this.container, zone, location);
    } catch (error) {
      console.error('ZIP code lookup failed:', error);
      this.container.innerHTML = `
        <div class="hardiness-zone-error">
          <p>Unable to find that ZIP code. Please try again or select your zone manually.</p>
          <button class="hardiness-zone-manual" onclick="hardinessZoneDetector.manualEntry()">
            Try Again
          </button>
        </div>
      `;
    }
  }

  async lookupZipCode() {
    const zipInput = document.getElementById('zip-input');
    const zip = zipInput ? zipInput.value.trim() : '';

    if (!zip || !/^\d{5}$/.test(zip)) {
      alert('Please enter a valid 5-digit ZIP code');
      return;
    }

    this.showLoading(this.container);

    try {
      const { zone, location } = await this._resolveZip(zip);
      this._cacheSet('hz_result', { zone, location });
      this.displayZone(this.container, zone, location);
    } catch (error) {
      console.error('ZIP code lookup failed:', error);
      this.container.innerHTML = `
        <div class="hardiness-zone-error">
          <p>Unable to find that ZIP code. Please try again or select your zone manually.</p>
          <button class="hardiness-zone-manual" onclick="hardinessZoneDetector.manualEntry()">
            Try Again
          </button>
        </div>
      `;
    }
  }

  manualEntry() {
    this.container.innerHTML = `
      <div class="hardiness-zone-manual-entry">
        <label for="zip-input">Enter your ZIP code to see your hardiness zone:</label>
        <input type="text"
               id="zip-input"
               class="hardiness-zone-zip-input"
               placeholder="Enter ZIP code"
               maxlength="5"
               pattern="[0-9]*">
        <button class="hardiness-zone-save" onclick="hardinessZoneDetector.lookupZipCode()">
          Find My Zone
        </button>
        <p class="hardiness-zone-or-text">Or</p>
        <button class="hardiness-zone-manual-select" onclick="hardinessZoneDetector.showZoneSelect()">
          Select zone manually
        </button>
      </div>
    `;
  }

  showZoneSelect() {
    this.container.innerHTML = `
      <div class="hardiness-zone-manual-entry">
        <label for="zone-select">Select your USDA hardiness zone:</label>
        <select id="zone-select" class="hardiness-zone-select">
          <option value="">Choose your zone...</option>
          <option value="1a">Zone 1a (-60°F to -55°F)</option>
          <option value="1b">Zone 1b (-55°F to -50°F)</option>
          <option value="2a">Zone 2a (-50°F to -45°F)</option>
          <option value="2b">Zone 2b (-45°F to -40°F)</option>
          <option value="3a">Zone 3a (-40°F to -35°F)</option>
          <option value="3b">Zone 3b (-35°F to -30°F)</option>
          <option value="4a">Zone 4a (-30°F to -25°F)</option>
          <option value="4b">Zone 4b (-25°F to -20°F)</option>
          <option value="5a">Zone 5a (-20°F to -15°F)</option>
          <option value="5b">Zone 5b (-15°F to -10°F)</option>
          <option value="6a">Zone 6a (-10°F to -5°F)</option>
          <option value="6b">Zone 6b (-5°F to 0°F)</option>
          <option value="7a">Zone 7a (0°F to 5°F)</option>
          <option value="7b">Zone 7b (5°F to 10°F)</option>
          <option value="8a">Zone 8a (10°F to 15°F)</option>
          <option value="8b">Zone 8b (15°F to 20°F)</option>
          <option value="9a">Zone 9a (20°F to 25°F)</option>
          <option value="9b">Zone 9b (25°F to 30°F)</option>
          <option value="10a">Zone 10a (30°F to 35°F)</option>
          <option value="10b">Zone 10b (35°F to 40°F)</option>
          <option value="11a">Zone 11a (40°F to 45°F)</option>
          <option value="11b">Zone 11b (45°F to 50°F)</option>
        </select>
        <button class="hardiness-zone-save" onclick="hardinessZoneDetector.saveManualZone()">
          Save
        </button>
      </div>
    `;
  }

  saveManualZone() {
    const select = document.getElementById('zone-select');
    const zone = select ? select.value : '';

    if (!zone) {
      alert('Please select a zone');
      return;
    }

    const option = select.options[select.selectedIndex];
    const tempRange = option.text.match(/\(([^)]+)\)/)[1];
    const zoneData = { zone, tempRange, description: 'Manually selected' };
    const location = { city: '', region: '', method: 'manual' };

    this._cacheSet('hz_result', { zone: zoneData, location });
    this.displayZone(this.container, zoneData, location);
  }

  changeZone() {
    this._cacheClear('hz_result');
    this.manualEntry();
  }

  checkProductCompatibility(userZone) {
    let minZone = document.querySelector('[data-min-zone]')?.dataset.minZone;
    let maxZone = document.querySelector('[data-max-zone]')?.dataset.maxZone;

    const zoneRange = document.querySelector('[data-zone-range]')?.dataset.zoneRange ||
                      document.querySelector('[data-usda-zone]')?.dataset.usdaZone;

    if (zoneRange && !minZone && !maxZone) {
      const cleanedRange = zoneRange.replace(/zones?\s*/i, '').trim();
      const rangeParts = cleanedRange.split('-').map(z => z.trim());
      if (rangeParts.length === 2) {
        minZone = rangeParts[0];
        maxZone = rangeParts[1];
      }
    }

    if (!minZone || !maxZone) return null;

    const zoneOrder = ['1a','1b','2a','2b','3a','3b','4a','4b','5a','5b',
                       '6a','6b','7a','7b','8a','8b','9a','9b','10a','10b','11a','11b'];

    const normalize = (z) => {
      z = z.toString().trim().toLowerCase();
      return /^\d+$/.test(z) ? z + 'a' : z;
    };

    const userIndex = zoneOrder.indexOf(normalize(userZone));
    const minIndex  = zoneOrder.indexOf(normalize(minZone));
    const maxIndex  = zoneOrder.indexOf(normalize(maxZone));

    if (userIndex === -1) return null;

    if (userIndex >= minIndex && userIndex <= maxIndex) {
      return { compatible: true,  message: `✓ This plant will grow in your zone!` };
    } else if (userIndex < minIndex) {
      return { compatible: false, message: `This plant thrives in Zone ${minZone} or higher. In your area, it should be treated as an annual or given adequate protection in the winter.` };
    } else {
      return { compatible: false, message: `⚠ This plant grows best in zones ${minZone}–${maxZone}. Your zone (${userZone}) may be too warm.` };
    }
  }

  showError(container) {
    container.innerHTML = `
      <div class="hardiness-zone-error">
        <p>Unable to detect your location.</p>
        <button class="hardiness-zone-manual" onclick="hardinessZoneDetector.manualEntry()">
          Enter zone manually
        </button>
      </div>
    `;
  }
}

if (document.querySelector('[data-hardiness-zone]')) {
  const hardinessZoneDetector = new HardinessZoneDetector();
  window.hardinessZoneDetector = hardinessZoneDetector;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => hardinessZoneDetector.init());
  } else {
    hardinessZoneDetector.init();
  }
}